import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { presenceAPI } from '../services/api';

interface PresenceStatus {
  is_checked_in: boolean;
  last_check_in: string | null;
  work_hours_today: number;
  status: string;
}

interface PresenceHistory {
  id: number;
  user_id: number;
  check_in: string;
  check_out: string | null;
  work_hours: number | null;
  overtime_hours: number | null;
  status: string;
  notes: string;
  user_first_name: string;
  user_last_name: string;
}

interface PresenceContextType {
  status: PresenceStatus | null;
  history: PresenceHistory[];
  loading: boolean;
  checkIn: (notes?: string) => Promise<void>;
  checkOut: (notes?: string) => Promise<void>;
  refreshStatus: () => Promise<void>;
  loadHistory: (from?: string, to?: string) => Promise<void>;
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (context === undefined) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};

interface PresenceProviderProps {
  children: ReactNode;
}

export const PresenceProvider: React.FC<PresenceProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<PresenceStatus | null>(null);
  const [history, setHistory] = useState<PresenceHistory[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      setLoading(true);
      const statusData = await presenceAPI.getStatus();
      setStatus(statusData);
    } catch (error) {
      // Errore nel caricamento status
    } finally {
      setLoading(false);
    }
  }, []);

  const checkIn = async (notes?: string) => {
    try {
      setLoading(true);
      await presenceAPI.checkIn(notes || '');
      await refreshStatus();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const checkOut = async (notes?: string) => {
    try {
      setLoading(true);
      await presenceAPI.checkOut(notes || '');
      await refreshStatus();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = useCallback(async (from?: string, to?: string) => {
    try {
      setLoading(true);
      const historyData = await presenceAPI.getHistory(from, to);
      setHistory(historyData.history || []);
    } catch (error) {
      setHistory([]); // Assicurati che sia sempre un array
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    loadHistory();
  }, []);

  const value = {
    status,
    history,
    loading,
    checkIn,
    checkOut,
    refreshStatus,
    loadHistory,
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};
